import { supabase } from "@/integrations/supabase/client";

// Session data interface - accumulates during visit
interface SessionData {
  session_id: string;
  anonymous_user_id: string;
  started_at: string;
  device: 'Desktop' | 'Mobile' | 'Tablet';
  region: string;
  utm_source: string | null;
  
  // Airtable record ID (set after initial create)
  airtable_record_id: string | null;
  
  // Engagement
  pages_viewed_count: number;
  scroll_depth_max: number;
  listing_view_time_seconds: number;
  
  // Listings
  listings_modal_opened: string[];
  listings_viewed: string[];  // Viewport impressions (deduplicated)
  
  // Search & Filters - V0 simplified
  search_keywords: string[];
  used_filter: boolean;
  filter_actions_count: number;
  filter_price_band: string | null;
  
  // Conversion
  request_private_showing: boolean;
  requested_listings: string[];  // Array for multiple requested listings
  request_count: number;  // Count of unique requested listings
  request_timestamp: string | null;
  
  // Language tracking
  used_th: boolean;  // True if Thai language was used at least once
}

const SESSION_STORAGE_KEY = 'ts_session_data';
const USER_KEY = 'ts_anonymous_user_id';
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const UPDATE_DEBOUNCE_MS = 2000; // Debounce updates to every 2 seconds

let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let updateTimeout: ReturnType<typeof setTimeout> | null = null;
let modalOpenTime: number = 0;

// Get or create anonymous user ID (persists across sessions)
function getAnonymousUserId(): string {
  let userId = localStorage.getItem(USER_KEY);
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem(USER_KEY, userId);
  }
  return userId;
}

// Get device type
function getDeviceType(): 'Mobile' | 'Tablet' | 'Desktop' {
  const width = window.innerWidth;
  if (width < 768) return 'Mobile';
  if (width < 1024) return 'Tablet';
  return 'Desktop';
}

// Get region from timezone
function getRegion(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const parts = timezone.split('/');
    return parts[parts.length - 1].replace(/_/g, ' ');
  } catch {
    return 'Unknown';
  }
}

// Get UTM source from URL or sessionStorage
function getUtmSource(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  if (utmSource) {
    sessionStorage.setItem('ts_utm_source', utmSource);
    return utmSource;
  }
  return sessionStorage.getItem('ts_utm_source');
}

// Get current session data from sessionStorage
function getSessionData(): SessionData | null {
  const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!stored) return null;
  try {
    const session = JSON.parse(stored);
    // Ensure array fields are initialized (handles legacy sessions without these properties)
    if (!Array.isArray(session.requested_listings)) {
      session.requested_listings = [];
    }
    if (!Array.isArray(session.listings_modal_opened)) {
      session.listings_modal_opened = [];
    }
    if (!Array.isArray(session.listings_viewed)) {
      session.listings_viewed = [];
    }
    if (!Array.isArray(session.search_keywords)) {
      session.search_keywords = [];
    }
    return session;
  } catch {
    return null;
  }
}

// Save session data to sessionStorage
function saveSessionData(data: SessionData): void {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
}

// Create new session object
function createSessionObject(): SessionData {
  return {
    session_id: crypto.randomUUID(),
    anonymous_user_id: getAnonymousUserId(),
    started_at: new Date().toISOString(),
    device: getDeviceType(),
    region: getRegion(),
    utm_source: getUtmSource(),
    airtable_record_id: null,
    pages_viewed_count: 0,
    scroll_depth_max: 0,
    listing_view_time_seconds: 0,
    listings_modal_opened: [],
    listings_viewed: [],
    search_keywords: [],
    used_filter: false,
    filter_actions_count: 0,
    filter_price_band: null,
    request_private_showing: false,
    requested_listings: [],
    request_count: 0,
    request_timestamp: null,
    used_th: false,
  };
}

// Format session data for Airtable
function formatSessionPayload(session: SessionData, includeEndedAt: boolean = false): Record<string, any> {
  const payload: Record<string, any> = {
    session_id: session.session_id,
    anonymous_user_id: session.anonymous_user_id,
    started_at: session.started_at,
    device: session.device,
    region: session.region,
    utm_source: session.utm_source || 'direct',
    pages_viewed_count: session.pages_viewed_count,
    scroll_depth_max: session.scroll_depth_max,
    listing_view_time_seconds: session.listing_view_time_seconds,
    used_filter: session.used_filter,
    filter_actions_count: session.filter_actions_count,
    request_private_showing: session.request_private_showing,
    request_count: session.request_count,
  };

  // Extra context for the Supabase admin dashboard mirror (ignored by Airtable mapping)
  try {
    payload.referrer = document.referrer || 'Direct';
    payload.landing_page = window.location.pathname || '/';
    payload.browser = navigator.userAgent.slice(0, 200);
  } catch {
    /* SSR / non-browser: ignore */
  }
  
  // Only include used_th if true (default is false/unchecked)
  if (session.used_th) {
    payload.used_th = true;
  }
  
  // Only include string fields if they have actual values (not empty)
  const searchKeywords = session.search_keywords.join(' | ');
  if (searchKeywords) {
    payload.search_keywords = searchKeywords;
  }
  
  if (session.filter_price_band) {
    payload.filter_price_band = session.filter_price_band;
  }
  
  if (session.request_timestamp) {
    payload.request_timestamp = session.request_timestamp;
  }
  
  // Only include linked record arrays if they have valid values
  if (session.listings_modal_opened.length > 0) {
    payload.listings_modal_opened = session.listings_modal_opened;
    console.log('[Analytics] formatSessionPayload - listings_modal_opened:', session.listings_modal_opened);
  }
  if (session.listings_viewed.length > 0) {
    payload.listings_viewed = session.listings_viewed;
  }
  if (session.requested_listings.length > 0) {
    payload.requested_listings = session.requested_listings;
    console.log('[Analytics] formatSessionPayload - requested_listings:', session.requested_listings);
  }
  
  if (session.airtable_record_id) {
    payload.record_id = session.airtable_record_id;
  }
  
  if (includeEndedAt) {
    payload.ended_at = new Date().toISOString();
  }
  
  // Debug: log the full payload summary
  console.log('[Analytics] formatSessionPayload summary:', {
    session_id: payload.session_id,
    modal_opened_count: session.listings_modal_opened.length,
    requested_count: session.requested_listings.length,
    has_record_id: !!payload.record_id
  });
  
  return payload;
}

// Create initial session record in Airtable
async function createSessionRecord(session: SessionData): Promise<string | null> {
  try {
    const payload = formatSessionPayload(session);
    
    const { data, error } = await supabase.functions.invoke('track-session', {
      body: payload
    });
    
    if (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to create session record:', error);
      }
      return null;
    }
    
    return data?.id || null;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to create session record:', error);
    }
    return null;
  }
}

// Update existing session record in Airtable
async function updateSessionRecord(session: SessionData): Promise<void> {
  if (!session.airtable_record_id) {
    console.warn('[Analytics] updateSessionRecord: No airtable_record_id, skipping update');
    return;
  }
  
  try {
    const payload = formatSessionPayload(session);
    console.log('[Analytics] updateSessionRecord - Sending to track-session:', JSON.stringify(payload, null, 2));
    
    const { data, error } = await supabase.functions.invoke('track-session', {
      body: payload
    });
    
    if (error) {
      console.error('[Analytics] updateSessionRecord error:', error);
    } else {
      console.log('[Analytics] updateSessionRecord success:', data);
    }
  } catch (error) {
    console.error('[Analytics] Failed to update session record:', error);
  }
}

// Schedule a debounced update
function scheduleUpdate(): void {
  if (updateTimeout) clearTimeout(updateTimeout);
  
  updateTimeout = setTimeout(() => {
    const session = getSessionData();
    if (session?.airtable_record_id) {
      updateSessionRecord(session);
    }
  }, UPDATE_DEBOUNCE_MS);
}

// Reset inactivity timer
function resetInactivityTimer(): void {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  inactivityTimer = setTimeout(() => {
    endSession();
  }, INACTIVITY_TIMEOUT);
}

// Send final session data (on page unload) - use fetch with keepalive for auth headers
function sendFinalUpdate(session: SessionData): void {
  if (!session.airtable_record_id) return;
  
  try {
    const payload = formatSessionPayload(session, true);
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-session`;
    
    // Use fetch with keepalive for reliable delivery with auth headers
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Silent fail on page unload
    });
  } catch {
    // Silent fail on page unload
  }
}

// End session
export function endSession(): void {
  const session = getSessionData();
  if (session) {
    sendFinalUpdate(session);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
}

// Initialize session - creates record in Airtable immediately
export async function initSession(): Promise<void> {
  let session = getSessionData();
  
  // If session exists with a record ID, it's still valid
  if (session?.airtable_record_id) {
    // Set up listeners
    setupListeners();
    return;
  }
  
  // Create new session
  session = createSessionObject();
  saveSessionData(session);
  
  // Create record in Airtable immediately
  const recordId = await createSessionRecord(session);
  if (recordId) {
    session.airtable_record_id = recordId;
    saveSessionData(session);
  }
  
  setupListeners();
}

// Set up event listeners
function setupListeners(): void {
  // Set up beforeunload handler
  window.addEventListener('beforeunload', () => {
    endSession();
  });
  
  // Set up inactivity timer
  resetInactivityTimer();
  
  // Reset timer on user activity
  ['click', 'scroll', 'keydown', 'mousemove', 'touchstart'].forEach(event => {
    window.addEventListener(event, resetInactivityTimer, { passive: true });
  });
}

// Track page view
export function trackPageView(): void {
  const session = getSessionData();
  if (!session) return;
  
  session.pages_viewed_count += 1;
  saveSessionData(session);
  resetInactivityTimer();
  scheduleUpdate();
}

// Track scroll depth
export function trackScroll(percent: number): void {
  const session = getSessionData();
  if (!session) return;
  
  if (percent > session.scroll_depth_max) {
    session.scroll_depth_max = percent;
    saveSessionData(session);
    scheduleUpdate();
  }
  resetInactivityTimer();
}

// Track listing viewport impression (once per session per listing)
export function trackListingView(listingId: string): void {
  const session = getSessionData();
  if (!session) return;
  
  // Deduplicate - only record once per session
  if (!session.listings_viewed.includes(listingId)) {
    session.listings_viewed.push(listingId);
    saveSessionData(session);
    scheduleUpdate();
  }
  resetInactivityTimer();
}

// Track modal open - main interest signal
export function trackModalOpen(listingId: string): void {
  const session = getSessionData();
  if (!session) {
    console.warn('[Analytics] trackModalOpen: No session found');
    return;
  }
  
  // Validate listingId format (should be Airtable record ID: rec + 14 chars)
  const isValidAirtableId = /^rec[a-zA-Z0-9]{14}$/.test(listingId);
  console.log('[Analytics] trackModalOpen called:', {
    listingId,
    isValidAirtableId,
    currentCount: session.listings_modal_opened.length,
    currentList: session.listings_modal_opened
  });
  
  if (!isValidAirtableId) {
    console.error('[Analytics] trackModalOpen: Invalid Airtable ID format!', listingId);
  }
  
  // Add to listings_modal_opened (deduplicated)
  if (!session.listings_modal_opened.includes(listingId)) {
    session.listings_modal_opened.push(listingId);
    console.log('[Analytics] Added to listings_modal_opened. New list:', session.listings_modal_opened);
  } else {
    console.log('[Analytics] Already in listings_modal_opened, skipping duplicate');
  }
  modalOpenTime = Date.now();
  saveSessionData(session);
  resetInactivityTimer();
  scheduleUpdate();
}

// Track modal close - adds dwell time
export function trackModalClose(): void {
  const session = getSessionData();
  if (!session || modalOpenTime === 0) return;
  
  const dwellSeconds = Math.round((Date.now() - modalOpenTime) / 1000);
  session.listing_view_time_seconds += dwellSeconds;
  modalOpenTime = 0;
  saveSessionData(session);
  resetInactivityTimer();
  scheduleUpdate();
}

// Track search
export function trackSearch(keyword: string): void {
  const session = getSessionData();
  if (!session || !keyword.trim()) return;
  
  if (!session.search_keywords.includes(keyword.trim())) {
    session.search_keywords.push(keyword.trim());
    saveSessionData(session);
    scheduleUpdate();
  }
  resetInactivityTimer();
}

// Track filter usage - V0 simplified (just boolean + count)
export function trackFilter(filterType: string, value: string): void {
  const session = getSessionData();
  if (!session) return;
  
  // V0: Simple tracking - mark filter used and count actions
  session.used_filter = true;
  session.filter_actions_count += 1;
  
  // Still track price band (useful for segmentation)
  if (filterType === 'price') {
    const priceBands: Record<string, string> = {
      '800k-1m': '<1M',
      '1m-1.5m': '1-2M',
      '1.5m-2m': '1-2M',
      '2m-2.5m': '2-3M',
      '2.5m-3m': '2-3M',
    };
    session.filter_price_band = priceBands[value] || '>3M';
  }
  
  saveSessionData(session);
  resetInactivityTimer();
  scheduleUpdate();
}

// Track Thai language usage - marks session as having used TH at least once
export function trackThaiLanguageUsed(): void {
  const session = getSessionData();
  if (!session || session.used_th) return; // Already marked, skip
  
  session.used_th = true;
  saveSessionData(session);
  scheduleUpdate();
}

// Track private showing request - appends to requested_listings array
export function trackPrivateShowingRequest(listingId: string): void {
  console.log('[Analytics] trackPrivateShowingRequest called with listingId:', listingId);
  
  const session = getSessionData();
  if (!session) {
    console.warn('[Analytics] trackPrivateShowingRequest: No session found');
    return;
  }
  
  console.log('[Analytics] Current requested_listings before:', session.requested_listings);
  
  session.request_private_showing = true;
  
  // Ensure requested_listings is an array (defensive check)
  if (!Array.isArray(session.requested_listings)) {
    session.requested_listings = [];
  }
  
  // Add to requested_listings (deduplicated)
  if (!session.requested_listings.includes(listingId)) {
    session.requested_listings.push(listingId);
    console.log('[Analytics] Added to requested_listings:', session.requested_listings);
  } else {
    console.log('[Analytics] Already in requested_listings, skipping');
  }
  
  // Update request_count to match array length
  session.request_count = session.requested_listings.length;
  
  // Update timestamp to latest request time (UTC)
  session.request_timestamp = new Date().toISOString();
  saveSessionData(session);
  resetInactivityTimer();
  
  console.log('[Analytics] Session after update - requested_listings:', session.requested_listings, 'request_count:', session.request_count);
  
  // Use fetch with keepalive for guaranteed delivery when user might navigate away to LINE
  if (session.airtable_record_id) {
    console.log('[Analytics] Sending immediate update with requested_listings using keepalive');
    const payload = formatSessionPayload(session);
    console.log('[Analytics] Payload being sent:', JSON.stringify(payload));
    
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-session`;
    
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify(payload),
      keepalive: true, // Ensures request completes even if page navigates to LINE
    }).then(response => {
      console.log('[Analytics] Keepalive request completed with status:', response.status);
    }).catch(err => {
      console.error('[Analytics] Keepalive request failed:', err);
    });
  } else {
    console.warn('[Analytics] No airtable_record_id yet, update will happen on next sync');
  }
}

// Scroll tracking with debounce
let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

export function initScrollTracking(): () => void {
  const handleScroll = () => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    
    scrollTimeout = setTimeout(() => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      trackScroll(scrollPercent);
    }, 300);
  };
  
  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', handleScroll);
    if (scrollTimeout) clearTimeout(scrollTimeout);
  };
}
